import React, { useState, useMemo, useCallback } from "react";
import { format, parseISO } from "date-fns";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "./styles.css";

export function RunsTable({ workflow, runs }) {
  const [, setGridApi] = useState(null); // interact with the grid (if you'd like)

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      sortingOrder: ["desc", "asc", null],
    }),
    []
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Job Type",
        field: "jobType",
        initialWidth: 240,
        resizable: true,
      },
      {
        headerName: "Created",
        field: "created_at",
        initialWidth: 500,
        resizable: true,
        valueFormatter: (params) =>
          format(parseISO(params.value), "MMMM do, yyyy h:mm a"),
      },
      {
        headerName: "Status",
        field: "status",
        initialWidth: 240,
        resizable: true,
        cellRenderer: (params) => {
          return (
            <span className={getBadgeStyleFromStatus(params.value)}>
              {params.value}
            </span>
          );
        },
      },
      {
        headerName: "Steps Completed",
        field: "stepsCompleted",
        flex: 1,
        initialWidth: 240,
        resizable: true,
        sortable: false,
        valueGetter: (params) => {
          const completedSteps = params.data.completedSteps
            ? params.data.completedSteps.length
            : 0;
          const totalSteps = workflow?.nodes ? workflow.nodes.length - 1 : 0;
          return `${completedSteps}/${totalSteps}`;
        },
      },
    ],
    [workflow]
  );

  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
  }, []);

  const defaultSortModel = [{ colId: "created_at", sort: "desc" }];

  return (
    <div className="flow-root">
      <h2 className="text-lg font-semibold text-gray-700 mb-4">Runs</h2>

      <p className="text-sm font-semibold text-gray-400 mb-8">
        Below are all of the runs for this workflow. A run is triggered by a
        supabase webhook response that passes the workflow's filter.
      </p>

      <div className="ag-theme-quartz ag-theme-rectangle">
        <AgGridReact
          columnDefs={columnDefs}
          rowData={runs}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          domLayout="autoHeight"
          animateRows={true}
          defaultSortModel={defaultSortModel}
        />
      </div>
    </div>
  );
}

function getBadgeStyleFromStatus(status) {
  const baseStyle =
    "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset";

  switch (status) {
    case "started":
      return `${baseStyle} bg-blue-50 text-blue-700 ring-blue-700/10`;
    case "running":
      return `${baseStyle} bg-yellow-50 text-yellow-800 ring-yellow-600/20`;
    case "completed":
      return `${baseStyle} bg-green-50 text-green-700 ring-green-600/20`;
    case "failed":
      return `${baseStyle} bg-red-50 text-red-700 ring-red-600/10`;
    default:
      return `${baseStyle} bg-gray-50 text-gray-600 ring-gray-500/10`;
  }
}
